// Public landing page, shown at "/" to signed-out visitors. Static content only:
// it makes no API calls, so it renders even when the engine is unreachable.
// SiteNav, SiteFooter and SimulatedMoney are shared with the pricing page.
import type { ReactNode } from "react";
import { Mark } from "../components/Shell";
import { consolePath, contactMailto, docsHome, marketingPath } from "../lib/site";

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

const features: { title: string; body: string; icon: ReactNode; tag?: string }[] = [
  {
    title: "Branded banks",
    body: "Run several banks per game, each with its own 3-digit code, colour, base currency, feature flags and system ledger accounts.",
    icon: <path d="M3 10l9-6 9 6M5 10v8M9.7 10v8M14.3 10v8M19 10v8M3 20h18" />,
  },
  {
    title: "Player accounts",
    body: "10-digit account numbers with a check digit, and name enquiry so players can confirm who they're paying before money moves.",
    icon: <><path d="M3 6h18v12H3z" /><path d="M7 10.5h5M7 14h7M17 10.5h.01" /></>,
  },
  {
    title: "Funding & transfers",
    body: "Pay salaries and rewards from a separate treasury key, and let players send money to each other within per-transaction and daily limits.",
    icon: <path d="M4 8h13l-3-3M20 16H7l3 3" />,
  },
  {
    title: "Fees & charges",
    tag: "New",
    body: "Fixed, percentage or combined fees and levies with caps, amount ranges and start dates. Quote them before a player pays; each one is its own statement line.",
    icon: <><path d="M20 12 12 20l-8-8V4h8z" /><circle cx="8.5" cy="8.5" r="1.5" /></>,
  },
  {
    title: "Debit cards",
    tag: "New",
    body: "Issue cards on player accounts, authorise payments at shops, capture or release them, refund in parts, and block or report a card lost. Declines come back with a reason.",
    icon: <><rect x="2.5" y="5" width="19" height="14" rx="2" /><path d="M2.5 10h19M6 15h4" /></>,
  },
  {
    title: "Merchants",
    tag: "New",
    body: "NPC shops and player-run stores get merchant accounts that take card payments, receive transfers and pay out, so every coin a shop earns is on the books.",
    icon: <><path d="M4 9l1.5-5h13L20 9" /><path d="M4 9h16v2a2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5.5 0A2.5 2.5 0 0 1 4 11z" /><path d="M5 13v7h14v-7" /></>,
  },
  {
    title: "Loans & credit scores",
    tag: "New",
    body: "Players borrow and repay in instalments on the game clock. Every player gets a 0–100 credit score from their money history, or bring your game's own score; late fees, defaults and payoffs included.",
    icon: <><path d="M3 15h4l4.5 2.5a2 2 0 0 0 1.5.2L20 16" /><path d="M7 15l3.5-3H14a1.5 1.5 0 0 1 0 3h-3" /><circle cx="16" cy="6" r="3" /></>,
  },
  {
    title: "Savings & interest",
    body: "Savings products you design: tiered rates, daily accrual on the game clock, monthly or quarterly payouts, withholding tax and withdrawal rules.",
    icon: <><path d="M4 18l5-5 4 4 7-8" /><path d="M15 9h5v5" /></>,
  },
  {
    title: "Fixed deposits",
    tag: "New",
    body: "Lock money away for a term at a fixed rate. At maturity it pays out or rolls over; break it early and the penalty you set applies, quoted first.",
    icon: <><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" /></>,
  },
  {
    title: "Webhooks & alerts",
    tag: "New",
    body: "Signed debit and credit alerts for every money movement, plus card and savings events, delivered with retries, redelivery and an events feed.",
    icon: <><path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" /><path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" /><path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" /></>,
  },
  {
    title: "Holds",
    body: "Authorise now, settle later. Place a hold for an auction bid or a rental, then capture all or part of it, or release it.",
    icon: <><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  },
  {
    title: "A game clock",
    body: "Real-time, scaled or manual. Holds, limits, card expiry and interest all follow game days, and every transaction records both clocks.",
    icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  },
  {
    title: "Reversals & statements",
    body: "Post compensating reversals instead of editing history, and read any account as a passbook with a running balance.",
    icon: <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />,
  },
];

const sdks: { lang: string; install: string }[] = [
  { lang: "TypeScript", install: "npm i @gamebanking/sdk" },
  { lang: "Python", install: "pip install game-banking-sdk" },
  { lang: "Go", install: "go get github.com/ShowBaba/game-banking-sdk-go" },
  { lang: "C# / .NET", install: "dotnet add package GameBanking" },
  { lang: "Rust", install: "cargo add gamebanking" },
];

const guarantees: { k: string; title: string; body: string }[] = [
  { k: "debits = credits", title: "Double-entry, always", body: "Every movement posts a balanced, atomic set of ledger entries. Entries are never edited, and balances are reconciled against the ledger." },
  { k: "Idempotency-Key", title: "Retries are safe", body: "Every money-moving request carries an idempotency key. Send it twice and you get the original result back, so money never moves twice." },
  { k: "amount_minor", title: "No floating point", body: "Amounts are integers in minor units with the currency's exponent. ₦2,500.00 is 250000, and a Gold coin with exponent 0 is just 1." },
  { k: "sandbox · production", title: "Isolated environments", body: "The environment comes from your API key, never the request body. Sandbox keys can't read or move production money." },
  { k: "GBE-Signature", title: "Alerts you can trust", body: "Every webhook is signed with HMAC-SHA256 and written in the same transaction as the money it reports, so an alert exists exactly when the money moved." },
  { k: "accrued_micro", title: "Interest to the millionth", body: "Interest accrues daily in millionths of a minor unit and is paid in whole units, so nothing is lost to rounding, even across a jump of the game clock." },
];

const steps: { title: string; body: string }[] = [
  { title: "Create a game", body: "Sign up for free and register your game. You get a sandbox straight away." },
  { title: "Issue scoped keys", body: "A game-server key for everyday play, and a separate treasury key that can create money." },
  { title: "Set up a bank", body: "Pick a code, a colour and a currency, then set limits and turn features on or off." },
  { title: "Call the API", body: "Open accounts, pay players, take card payments and pay interest from your game server, with an SDK or plain HTTP." },
];

export function SiteNav({ page }: { page: "home" | "pricing" }) {
  const home = page === "home" ? "" : marketingPath("/");
  return (
    <header className="lp-nav">
      <div className="lp-wrap lp-nav-row">
        <a className="lp-brand" href={marketingPath("/")}>
          <Mark size={30} />
          <span>Game Banking Engine</span>
        </a>
        <nav aria-label="Site" className="lp-links">
          <a href={`${home}#features`}>Features</a>
          <a href={`${home}#how`}>How it works</a>
          <a href={`${home}#sdks`}>SDKs</a>
          <a href={marketingPath("/pricing")} aria-current={page === "pricing" ? "page" : undefined}>Pricing</a>
          <a href={docsHome()}>Docs</a>
        </nav>
        <div className="lp-nav-cta">
          <a className="btn ghost" href={consolePath("/login")}>Sign in</a>
          <a className="btn primary" href={consolePath("/signup")}>Get sandbox access</a>
        </div>
      </div>
    </header>
  );
}

/** States plainly that the engine handles in-game money only. */
export function SimulatedMoney({ alt }: { alt?: boolean }) {
  return (
    <section className={`lp-section${alt ? " lp-alt" : ""}`} id="simulated">
      <div className="lp-wrap">
        <div className="lp-head">
          <span className="lp-eyebrow">In-game money only</span>
          <h2>A simulation, not a bank.</h2>
          <p>
            Game Banking Engine runs banking for currencies that exist only inside your game. It isn't a bank, it doesn't
            hold or move real money, and it isn't licensed or regulated as a financial service.
          </p>
        </div>
        <div className="lp-roles">
          <article className="lp-role">
            <span className="badge role">Built for</span>
            <h3>Money that lives in your game</h3>
            <ul>
              <li>In-game currencies: game naira, gold, gems, credits</li>
              <li>Salaries, rewards, shops, savings, loans and cards inside your world</li>
              <li>Currencies players buy with real money, as long as they can't turn them back into real money</li>
              <li>Testing, prototyping and teaching how banking works</li>
            </ul>
          </article>
          <article className="lp-role lp-not">
            <span className="badge role">Not for</span>
            <h3>Anything that touches real money</h3>
            <ul>
              <li>Holding, sending or settling real money, or real bank accounts</li>
              <li>Balances players can cash out, withdraw or exchange for real money or crypto</li>
              <li>Real card payments: card numbers are simulated and work on no real payment network</li>
              <li>Storing real people's financial or identity details</li>
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
}

export function SiteFooter() {
  return (
    <footer className="lp-foot">
      <div className="lp-wrap lp-foot-row">
        <span className="lp-brand small"><Mark size={22} /> Game Banking Engine</span>
        <nav aria-label="Footer">
          <a href={docsHome()}>Developer guide</a>
          <a href={marketingPath("/pricing")}>Pricing</a>
          <a href={contactMailto()}>Contact</a>
          <a href="/v1/openapi.yaml">OpenAPI spec</a>
          <a href={consolePath("/login")}>Sign in</a>
        </nav>
      </div>
      <div className="lp-wrap">
        <p className="lp-foot-note">
          Game Banking Engine simulates banking for in-game currencies. It is not a bank, holds no real money and is not a
          regulated financial service. Balances, cards and interest exist only inside the games that use it.
        </p>
      </div>
    </footer>
  );
}

export function Landing() {
  return (
    <div className="landing">
      <SiteNav page="home" />

      <main>
        <section className="lp-hero">
          <div className="lp-wrap lp-hero-grid">
            <div>
              <span className="lp-eyebrow">Banking infrastructure for game economies</span>
              <h1>Run the bank behind your game.</h1>
              <p className="lp-lede">
                Give your game server a REST API for branded banks, player accounts, transfers, debit cards, shops,
                savings with interest, loans with credit scores and signed alerts. Every movement posts to an immutable double-entry ledger, stamped
                with your game's own clock.
              </p>
              <div className="lp-cta">
                <a className="btn primary lp-big" href={consolePath("/signup")}>Start in the sandbox</a>
                <a className="btn lp-big lp-ghost-dark" href={docsHome()}>Read the developer guide</a>
              </div>
              <p className="lp-fine">Free sandbox. No card needed. For in-game money only: no real funds ever move.</p>
            </div>

            <div className="lp-hero-art" aria-label="Example transfer request and the posting it creates">
              <div className="lp-code">
                <div className="lp-code-bar">
                  <span className="lp-dots" aria-hidden="true"><i /><i /><i /></span>
                  <span>POST /v1/transfers</span>
                </div>
                <pre><code>
<span className="h">Idempotency-Key</span>: order-77{"\n\n"}
{"{\n"}
{"  "}<span className="h">"source_account_id"</span>: <span className="s">"3f8f0b9c-…"</span>,{"\n"}
{"  "}<span className="h">"destination_account_number"</span>: <span className="s">"5915945174"</span>,{"\n"}
{"  "}<span className="h">"amount_minor"</span>: <span className="n">320000</span>,{"\n"}
{"  "}<span className="h">"narration"</span>: <span className="s">"Market purchase"</span>{"\n"}
{"}"}
                </code></pre>
              </div>
              <div className="ticket lp-ticket">
                <div className="t-row"><span>Session</span><span>058261013201613393</span></div>
                <div className="t-row"><span>Game time</span><span>01 Jun 2040 09:00</span></div>
                <div className="t-rule" />
                <div className="t-row"><span>Dr  Ada Obi  0853191403</span><span>₦3,200.00</span></div>
                <div className="t-row"><span>Cr  Tunde Bello  5915945174</span><span>₦3,200.00</span></div>
                <div className="t-rule" />
                <div className="t-row"><span>Debits equal credits</span><span>Posted</span></div>
              </div>
            </div>
          </div>

          <div className="lp-wrap">
            <ul className="lp-pills" aria-label="Highlights">
              <li>Double-entry ledger</li>
              <li>Idempotent writes</li>
              <li>Debit cards &amp; merchants</li>
              <li>Savings &amp; fixed deposits</li>
              <li>Loans &amp; credit scores</li>
              <li>Signed webhooks</li>
              <li>SDKs in 5 languages</li>
            </ul>
          </div>
        </section>

        <section className="lp-section" id="features">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">What you get</span>
              <h2>Everything a bank does, sized for a game.</h2>
              <p>Model the money in your world the way real banks do, without building a ledger yourself.</p>
            </div>
            <div className="lp-cards">
              {features.map((f) => (
                <article className="lp-card" key={f.title}>
                  <span className="lp-icon"><Glyph>{f.icon}</Glyph></span>
                  <h3>{f.title}{f.tag && <span className="lp-tag">{f.tag}</span>}</h3>
                  <p>{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-alt" id="how">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">How it works</span>
              <h2>From sign-up to first transfer in an afternoon.</h2>
              <p>
                Players talk to your game server, and your game server talks to the engine. Game API keys never ship in a
                client build.
              </p>
            </div>
            <ol className="lp-steps">
              {steps.map((s, i) => (
                <li key={s.title}>
                  <span className="lp-step-n">{i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
            <div className="lp-playground">
              <div>
                <h3>Try every money movement before a player does.</h3>
                <p>
                  The sandbox playground turns your browser into a game server with a short-lived sandbox key. Open accounts
                  and shops, pay players, issue cards and pay by card, open savings and fixed deposits, take out loans, then jump
                  the game clock and watch interest arrive and instalments get collected in a printed passbook. Ending the session revokes the key.
                </p>
              </div>
              <a className="btn primary lp-big" href={consolePath("/signup")}>Open the playground</a>
            </div>
          </div>
        </section>

        <section className="lp-section" id="sdks">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">Official SDKs</span>
              <h2>Your language, the same guarantees.</h2>
              <p>Every SDK sends idempotency keys and reuses them on retries, maps errors to types, pages through lists and verifies webhooks.</p>
            </div>
            <div className="lp-sdks">
              {sdks.map((x) => (
                <article key={x.lang}>
                  <h3>{x.lang}</h3>
                  <code>{x.install}</code>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section" id="trust">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">The ledger</span>
              <h2>Books that always balance.</h2>
              <p>The guarantees you'd expect from a bank, enforced by the engine rather than left to each integration.</p>
            </div>
            <div className="lp-guarantees">
              {guarantees.map((g) => (
                <article key={g.title}>
                  <code>{g.k}</code>
                  <h3>{g.title}</h3>
                  <p>{g.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <SimulatedMoney alt />

        <section className="lp-section">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">One app, two roles</span>
              <h2>A console for every hat you wear.</h2>
            </div>
            <div className="lp-roles">
              <article className="lp-role">
                <span className="badge role">Developer portal</span>
                <h3>Build and test your economy</h3>
                <ul>
                  <li>Create games and issue sandbox or production keys with explicit scopes</li>
                  <li>Configure banks, limits and custom currencies like gold or gems</li>
                  <li>Design fee schedules, card programs, savings and loan products</li>
                  <li>Send signed alerts to your servers and replay any event</li>
                  <li>Run the game clock in real time, faster, or by hand</li>
                  <li>Watch activity and experiment in the sandbox playground</li>
                </ul>
              </article>
              <article className="lp-role" data-role="admin">
                <span className="badge role">Super admin console</span>
                <h3>Operate the whole platform</h3>
                <ul>
                  <li>Platform totals, money in circulation and 14-day ledger activity</li>
                  <li>Register, suspend and reactivate developers and games</li>
                  <li>Revoke any API key in an emergency, with an audited reason</li>
                  <li>Read the append-only audit log and run reconciliation</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="lp-final">
          <div className="lp-wrap">
            <h2>Ready to open your first bank?</h2>
            <p>Create a developer account, register a game and make your first transfer in the sandbox.</p>
            <div className="lp-cta">
              <a className="btn primary lp-big" href={consolePath("/signup")}>Create a developer account</a>
              <a className="btn lp-big lp-ghost-dark" href={docsHome()}>Read the developer guide</a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
